<script setup lang="ts">
import {
  EnumerationEvaluationOptionDto,
  EvaluationParameterDto,
  instanceOfEnumerationEvaluationParameterDto,
  instanceOfEnumerationEvaluationResultDto,
  instanceOfNumericEvaluationParameterDto,
  instanceOfNumericEvaluationResultDto,
  ManualTaskWorkResultDto,
  ManualTaskWorkStepDto,
} from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import CommentInlineDialog from '@/components/comments/CommentInlineDialog.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { edit } from '@/components/icons/icons';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { I18nKey } from '@/utils/typings';
import { useManualTaskStep } from '@/views/jobOccurrences/utils/useManualTask';
import { getSetupParameterName } from '@/views/tasks/utils.ts';
import {
  getFormattingOptionsFromEvaluationParameter,
  NumberDisplayOptions,
} from '@/utils/numberUtils';
import { mdiClose, mdiEye, mdiEyeOff } from '@mdi/js';
import { computed, ref, toRef } from 'vue';
import { useEditingMode } from './useEditingMode';
import JobExecutionTrendViewDialog from '../../JobExecutionTrendViewDialog.vue';
import EnumInput from './EnumInput.vue';
import ManualTaskResult from './ManualTaskResult.vue';
import NumericInput from './NumericInput.vue';

const props = defineProps<{
  step: ManualTaskWorkStepDto;
  jobOccurrenceId: string;
}>();

const mode = useFormMode();

const { results, toggleIgnoreResult, addInput, updateResult, updating, reload } = useManualTaskStep(
  toRef(props, 'step'),
);

const repeated = ref<EvaluationParameterDto | undefined>(undefined);
const { isEditingAnyResult, enableEditing, disableEditing } = useEditingMode();

const isExecutable = computed(() => props.step.isExecutable);

const isInputDisabled = computed(
  () =>
    props.step.workState === 'Skipped' ||
    updating.value ||
    mode.value === 'details' ||
    !isExecutable.value,
);

function cancelRepeat() {
  repeated.value = undefined;
  isEditingAnyResult.value = false;
}

function isRepeated(evaluation: EvaluationParameterDto) {
  return evaluation === repeated.value;
}

function isResultIngored(result: ManualTaskWorkResultDto) {
  return result.state === 'Ignored';
}

function repeat(evaluation: EvaluationParameterDto) {
  repeated.value = evaluation;
  isEditingAnyResult.value = true;
}

function isRepeatShown(evaluation: EvaluationParameterDto) {
  return mode.value === 'execution' && results.value[evaluation.id] && !isRepeated(evaluation);
}

function isInputShown(evaluation: EvaluationParameterDto) {
  return !results.value[evaluation.id] || isRepeated(evaluation);
}

async function update(
  evaluation: EvaluationParameterDto,
  input: EnumerationEvaluationOptionDto | number,
) {
  const submitted = await addInput(evaluation, input);

  if (submitted) {
    repeated.value = undefined;
    isEditingAnyResult.value = false;
  }
}

function getEffectiveFormat(evaluation: EvaluationParameterDto): NumberDisplayOptions | undefined {
  if (!instanceOfNumericEvaluationParameterDto(evaluation)) {
    return undefined;
  }

  return getFormattingOptionsFromEvaluationParameter(evaluation);
}
</script>

<template>
  <v-sheet
    v-for="evaluation in step.evaluations"
    :key="evaluation.id"
    border
    class="pa-3 mb-4 elevation-2"
  >
    <v-row>
      <v-col cols="4">
        <v-row>
          <v-col cols="6">
            <v-label>
              {{
                $t(
                  instanceOfEnumerationEvaluationParameterDto(evaluation)
                    ? 'Enumeration_one'
                    : 'Quantity_one',
                )
              }}
            </v-label>

            <div>
              <IdentifierLabel :label="getSetupParameterName(evaluation)" />
            </div>
          </v-col>

          <v-col
            v-if="instanceOfNumericEvaluationParameterDto(evaluation) && evaluation.evaluationType"
            cols="6"
          >
            <v-label>{{ $t('EvaluationType_one') }}</v-label>
            <div>
              {{ $t(`EvaluationTypeEnum.${evaluation.evaluationType}` as I18nKey) }}
            </div>
          </v-col>

          <v-col v-if="evaluation.instruction" cols="6">
            <v-label>{{ $t('Instruction_one') }}</v-label>
            <div>
              {{ evaluation.instruction ?? EMPTY_FIELD_SIGN }}
            </div>
          </v-col>
        </v-row>
      </v-col>

      <v-col class="align-center">
        <div v-if="results[evaluation.id]">
          <div v-for="(result, index) in results[evaluation.id]" :key="result.id">
            <ManualTaskResult
              :result="result"
              :evaluation="evaluation"
              class="executionPanel"
              @enableEditing="enableEditing"
              @disableEditing="disableEditing"
            >
              <template #edit="{ toggleInEditing, evaluationResult }">
                <div class="d-flex align-end">
                  <EnumInput
                    v-if="instanceOfEnumerationEvaluationParameterDto(evaluation)"
                    :evaluation="evaluation"
                    :result="
                      instanceOfEnumerationEvaluationResultDto(evaluationResult)
                        ? evaluationResult
                        : undefined
                    "
                    @input="
                      updateResult(result, $event);
                      toggleInEditing();
                    "
                  />

                  <NumericInput
                    v-if="
                      instanceOfNumericEvaluationParameterDto(evaluation) &&
                      result.evaluationResult &&
                      instanceOfNumericEvaluationResultDto(result.evaluationResult)
                    "
                    :evaluation="evaluation"
                    :value="result.evaluationResult.deviationValue"
                    :stepId="step.id"
                    :jobOccurrenceId="jobOccurrenceId"
                    :formatValue="getEffectiveFormat(evaluation)"
                    @input="
                      updateResult(result, $event);
                      toggleInEditing();
                    "
                  />

                  <Tooltip>
                    <template #target="{ props: targetProps }">
                      <Button
                        v-bind="targetProps"
                        class="cancel-button"
                        aria-label="cancel"
                        variant="text"
                        size="small"
                        :icon="mdiClose"
                        @click="toggleInEditing(false)"
                      />
                    </template>
                    <template #tooltip>
                      {{ $t('Cancel') }}
                    </template>
                  </Tooltip>
                </div>
              </template>

              <template #actions="{ evaluationResult, toggleInEditing, disabled, isResultIgnored }">
                <div class="mr-3">
                  <JobExecutionTrendViewDialog
                    :evaluationResultId="evaluationResult.id"
                    :disabled="disabled || isResultIgnored || isEditingAnyResult"
                  />

                  <CommentInlineDialog
                    :entity="evaluationResult"
                    entityWithCommentType="EvaluationResultOrDataPoint"
                    :disabled="disabled || isEditingAnyResult"
                    @change="reload()"
                  />

                  <Tooltip>
                    <template #target="{ props: targetProps }">
                      <Button
                        v-if="mode === 'execution'"
                        v-bind="targetProps"
                        variant="text"
                        :icon="edit"
                        class="ml-6"
                        aria-label="edit"
                        :disabled="
                          disabled || isResultIgnored || !isExecutable || isEditingAnyResult
                        "
                        @click.stop="toggleInEditing()"
                      />
                    </template>
                    <template #tooltip>
                      {{ $t('Edit') }}
                    </template>
                  </Tooltip>

                  <Tooltip>
                    <template #target="{ props: targetProps }">
                      <Button
                        v-if="mode === 'execution'"
                        v-bind="targetProps"
                        variant="text"
                        :icon="isResultIngored(result) ? mdiEyeOff : mdiEye"
                        aria-label="ignore"
                        :disabled="disabled || !isExecutable || isEditingAnyResult"
                        @click.stop="toggleIgnoreResult(result)"
                      />
                    </template>
                    <template #tooltip>
                      {{ isResultIngored(result) ? $t('Ignored') : $t('Acknowledged') }}
                    </template>
                  </Tooltip>
                </div>
              </template>
            </ManualTaskResult>

            <div cols="2" class="d-flex justify-end">
              <Button
                v-if="
                  isExecutable &&
                  isRepeatShown(evaluation) &&
                  index === (results[evaluation.id].length ?? 0) - 1
                "
                variant="secondary"
                :label="$t('Repeat')"
                size="small"
                :disabled="isEditingAnyResult"
                @click="repeat(evaluation)"
              />
            </div>
          </div>
        </div>

        <div v-if="isInputShown(evaluation)" class="d-flex align-end executionPanel">
          <div>
            <EnumInput
              v-if="instanceOfEnumerationEvaluationParameterDto(evaluation)"
              :evaluation="evaluation"
              :disabled="isInputDisabled"
              @input="update(evaluation, $event)"
            />
          </div>

          <div class="w-25">
            <NumericInput
              v-if="instanceOfNumericEvaluationParameterDto(evaluation)"
              :evaluation="evaluation"
              :stepId="step.id"
              :jobOccurrenceId="jobOccurrenceId"
              :disabled="isInputDisabled"
              :formatValue="getEffectiveFormat(evaluation)"
              @input="update(evaluation, $event)"
            />
          </div>

          <Tooltip>
            <template #target="{ props: targetProps }">
              <Button
                v-if="isRepeated(evaluation)"
                v-bind="targetProps"
                variant="text"
                class="cancel-button"
                size="small"
                aria-label="Cancel repeat"
                :icon="mdiClose"
                @click="cancelRepeat"
              />
            </template>
            <template #tooltip>
              {{ $t('Cancel') }}
            </template>
          </Tooltip>
        </div>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<style lang="scss" scoped>
.executionPanel {
  position: relative;
  .cancel-button {
    position: absolute;
    top: 3px;
    right: 3px;
  }
}
</style>
